<template>
    <div class="step5-categories-copy-from-previous">
        <a class="is-inline-block mb-3" @click="emit('back')">
            <span class="icon"><i class="fas fa-arrow-left"></i></span>
            <span>{{ translate("page.category_manager.back_to_options") }}</span>
        </a>

        <div class="field">
            <label class="label">{{ translate("page.category_manager.choose_event") }}</label>
            <EventsSelect :events="events" v-model="selectedEventId" />
        </div>

        <template v-if="selectedEventId">
            <p v-if="!loadingCategories && categories.length > 0" class="mb-2">
                {{ translate("page.category_manager.total_categories_to_import") }} {{ categories.length }}
            </p>
            <CategoriesPreviewList
                :categories="categories.map(c => c.value)"
                :loading="loadingCategories"
                :show-empty-state="true"
                :empty-state-message="translate('page.category_manager.no_categories_to_import')"
            />
        </template>

        <div class="field mt-4 is-flex is-justify-content-end">
            <LoadingButton
                class="button is-primary"
                :disabled="!canContinue"
                :is-loading="false"
                data-testid="wizard-copy-continue"
                @click="copyAndContinue"
            >
                <span>{{ translate("wizard.step5.select_and_continue") }}</span>
                <span class="icon"><i class="fas fa-arrow-right"></i></span>
            </LoadingButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from "vue";
    import LoadingButton from "../../../Common/LoadingButton.vue";
    import EventsSelect from "../../../Common/Events/EventsSelect.vue";
    import CategoriesPreviewList from "../../../CategoriesManager/CategoriesPreviewList.vue";
    import { translate } from "../../../Common/translations";
    import { handleError } from "../../../Common/errorHandler";
    import eventsApi from "../../../Api/events";
    import categoriesApi from "../../../Api/Categories/categories";
    import type { CompactEvent, Category } from "../../../Common/types";
    import { useWizardStore } from "../wizardStore";

    const emit = defineEmits<{
        back: [];
        copied: [payload: { sourceEventId: number; previewCount: number; summaryLabel: string }];
    }>();

    const store = useWizardStore();

    const events = ref<CompactEvent[]>([]);
    const selectedEventId = ref<number | null>(store.categoriesSourceEventId ?? 0);
    const categories = ref<Category[]>([]);
    const loadingCategories = ref(false);

    const canContinue = computed(() => !!selectedEventId.value && selectedEventId.value > 0 && categories.value.length > 0);

    onMounted(() => {
        return eventsApi
            .getEventsToManageForOrganizer(undefined, true)
            .then(groupedEvents => {
                events.value = ([] as CompactEvent[])
                    .concat(groupedEvents.incomingEvents, groupedEvents.draftEvents, groupedEvents.pastEvents)
                    .sort((a, b) => +new Date(b.eventDate.startDate) - +new Date(a.eventDate.startDate));
            })
            .catch(handleError);
    });

    watch(
        selectedEventId,
        () => {
            if (!selectedEventId.value || selectedEventId.value < 1) {
                categories.value = [];
                return;
            }

            loadingCategories.value = true;
            return categoriesApi
                .getCategories(selectedEventId.value)
                .then(categoriesResponse => {
                    categories.value = categoriesResponse;
                })
                .catch(handleError)
                .finally(() => {
                    loadingCategories.value = false;
                });
        },
        { immediate: true },
    );

    function copyAndContinue() {
        if (!canContinue.value || !selectedEventId.value) {
            return;
        }

        const selectedEvent = events.value.find(e => e.id === selectedEventId.value);

        emit("copied", {
            sourceEventId: selectedEventId.value,
            previewCount: categories.value.length,
            summaryLabel: selectedEvent?.fullName ?? "",
        });
    }
</script>
